import { GlobalStore } from '@highlight/ipc'
import { useModels as useModelsStore, useFetchModels } from '../store'
import { MentionableModel } from '../types'
import { useEffect, useRef, useState } from 'react'

/**
 * A shared hook that manages both models and the default model.
 * This hook provides a consistent way to access model data throughout the application
 * and handles fetching both models and the default model.
 *
 * @param options Configuration options for the hook
 * @param options.autoFetch Whether to automatically fetch models and default model on mount
 * @param options.refreshBeforeRequest Whether to refresh the default model before each request
 * @returns An object containing models, default model ID, and functions to fetch them
 */
export const useModels = (
  options: {
    autoFetch?: boolean
    refreshBeforeRequest?: boolean
  } = {},
) => {
  const { autoFetch = true, refreshBeforeRequest = false } = options

  const fetchModels = useFetchModels()
  const models = useModelsStore()
  const defaultModelId = GlobalStore.settings().get('defaultModel') as string

  const [modelsState, setModelsState] = useState<MentionableModel[]>([])
  const [latestDefaultModelId, setLatestDefaultModelId] = useState<string | null>(defaultModelId)
  const initRef = useRef(false)

  // Auto-fetch models and default model on mount if autoFetch is true
  useEffect(() => {
    const initModels = async () => {
      if (autoFetch && !models.length && !initRef.current) {
        console.log('Initializing models store (once)...')
        initRef.current = true
        await fetchModels()
      }
    }

    initModels()
  }, [autoFetch, fetchModels, models.length])

  // Update models state when models change
  useEffect(() => {
    setModelsState(models)
  }, [models])

  // Update latest default model ID when default model ID changes
  useEffect(() => {
    setLatestDefaultModelId(defaultModelId)
  }, [defaultModelId])

  /**
   * Refreshes the default model and returns the latest default model ID.
   * This is useful for ensuring the most up-to-date default model is used for each request.
   */
  const refreshDefaultModel = async (): Promise<string | null> => {
    await fetchModels()
    // Re-read from the store after fetching: fetchModels may have migrated a
    // deprecated default to its replacement, so the closure-captured
    // defaultModelId can be stale here.
    const latest = GlobalStore.settings().get('defaultModel') as string
    setLatestDefaultModelId(latest)
    return latest
  }

  return {
    models: modelsState,
    defaultModelId: refreshBeforeRequest ? latestDefaultModelId : defaultModelId,
    fetchModels,
    refreshDefaultModel,
  }
}
